import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { getRumSessionEnabled } from 'src/utils/datadogRum';
import { sharedInitOptions } from './datadog-config';

/**
 * Whether a RUM event should be ignored.
 * @see https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
 */
function shouldIgnoreEvent(event: RumEvent) {
  // Never ignore events which aren't errors
  if (event.type !== 'error') {
    return false;
  }

  // Ignore errors from bots (which often have issues such as
  // failing to load Google Tag Manager, Zendesk, LaunchDarkly, etc)
  if (event.device?.type === 'bot') {
    return true;
  }

  const errorMessage = event.error.message;
  const inBackground = !event.view.in_foreground;
  // "Load failed" appears if there is a loss of network or user navigates away
  // from page (especially on iOS)
  const isSafariFetchAbortError =
    inBackground && errorMessage.includes('Load failed');

  // External tools (e.g. Google Tag Manager, Zendesk, LaunchDarkly, Pendo) are
  // often blocked by ad blockers or fail to load due to network issues (e.g.
  // loss of network, user navigating away from page).
  const isExternalToolNetworkIssue =
    errorMessage.includes('Failed to load https://www.googletagmanager.com') || // Analytics
    errorMessage.includes(
      'https://static.zdassets.com/ekr/sentry-browser.min.js',
    ) || // Zendesk
    errorMessage.includes('Error fetching flag settings: network error') || // LaunchDarkly
    errorMessage.includes('[LaunchDarkly] network error') || // LaunchDarkly
    errorMessage.includes('Failed to fetch recording config:Failed to fetch'); // Pendo recordings config

  // GQL requests are aborted when the user navigates away from the page
  const abortedGqlRequest =
    event.error.source === 'network' &&
    /api.external(-(stage|test)?)?.sideinc.(com|dev)/.test(
      event.error.resource?.url ?? '',
    ) &&
    (errorMessage.includes('signal is aborted without reason') ||
      errorMessage.includes('AbortError: Fetch is aborted') ||
      errorMessage.includes('AbortError: The user aborted a request'));

  // Filter out error that might be caused by link validation pre-fetch
  // rendering from email clicks. See this issue for more details:
  // https://github.com/DataDog/browser-sdk/issues/2715#issuecomment-2359950290.
  const linkValidationRegex =
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
  if (linkValidationRegex.test(errorMessage)) {
    return true;
  }

  return (
    isSafariFetchAbortError || isExternalToolNetworkIssue || abortedGqlRequest
  );
}

export async function initDatadog() {
  // These will log errors if missing env vars and be picked up by Datadog RUM
  if (!process.env.NEXT_PUBLIC_APP_VERSION) {
    // biome-ignore lint/suspicious/noConsole: come back to this
    console.error('[Side RUM]: Missing APP_VERSION variable');
  }

  if (!process.env.NEXT_PUBLIC_APP_ENV) {
    // biome-ignore lint/suspicious/noConsole: come back to this
    console.error('[Side RUM]: Missing APP_ENV variable');
  }

  const rumSessionEnabled = await getRumSessionEnabled();
  datadogRum.init({
    ...sharedInitOptions,
    allowedTracingUrls: [
      // graph-api
      {
        match: process.env.NEXT_PUBLIC_GRAPH_API || '',
        propagatorTypes: ['datadog', 'tracecontext'],
      },
      // Service calls via front end hooks
      {
        match: (url: string) => {
          const { origin, pathname } = new URL(url);
          return (
            // Current origin
            origin === window?.location.origin &&
            // Proxy paths (/api/proxies/*) and auth endpoints (/api/auth/*)
            pathname.startsWith(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/`)
          );
        },
        propagatorTypes: ['datadog', 'tracecontext'], // cspell:disable-line
      },
    ],
    applicationId: '44d4a88b-1fba-468a-83ae-98c9b276b813',
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    // define var conditionalSampleRate as 0 if the userAgent matches a pattern in botPatterns
    // or is a non /identity path otherwise, define conditionalSampleRate as 100
    sessionSampleRate: rumSessionEnabled ? 100 : 0,
    telemetrySampleRate: rumSessionEnabled ? 100 : 0,
    sessionReplaySampleRate: 0, // Not currently using replays
    service: 'identity-ui',
    site: 'datadoghq.com',
    version: process.env.NEXT_PUBLIC_APP_VERSION,
    // Feature flag usage tracking
    // NOTE: Inspector is enabled in src/modules/launchdarkly/provider.tsx
    enableExperimentalFeatures: ['feature_flags'],
    beforeSend: (event) => !shouldIgnoreEvent(event),
  });
}
